'use client'

import { useEffect, useRef, useState } from 'react'
import {
  MAX_PROJECT_ZONES, PROJECT_GLAZING, PROJECT_PRIORITIES,
  projectChecklistQuoteHref, projectChecklistSummary, type ProjectZone,
} from '@/lib/commercial/project-checklist'
import styles from './project-checklist.module.css'

type EditableZone = ProjectZone & { id: number }
const blankZone = (id: number): EditableZone => ({ id, label: '', priority: 'mixed', glazing: 'unknown', notes: '' })

export default function FilmProjectChecklist() {
  const [zones, setZones] = useState<EditableZone[]>([blankZone(1)])
  const [copyStatus, setCopyStatus] = useState('')
  const nextId = useRef(2)
  const focusAfterChange = useRef<number | null>(null)
  const summaryRef = useRef<HTMLTextAreaElement>(null)
  const summary = projectChecklistSummary(zones)

  useEffect(() => {
    if (focusAfterChange.current !== null) {
      document.getElementById(`zone-${focusAfterChange.current}-label`)?.focus()
      focusAfterChange.current = null
    }
  }, [zones.length])

  function updateZone(id: number, patch: Partial<ProjectZone>) {
    setZones(previous => previous.map(zone => zone.id === id ? { ...zone, ...patch } : zone))
    setCopyStatus('')
  }

  function addZone() {
    if (zones.length >= MAX_PROJECT_ZONES) return
    const id = nextId.current++
    focusAfterChange.current = id
    setZones(previous => [...previous, blankZone(id)])
    setCopyStatus('')
  }

  function removeZone(id: number) {
    if (zones.length <= 1) return
    const index = zones.findIndex(zone => zone.id === id)
    focusAfterChange.current = zones[index + 1]?.id ?? zones[index - 1]?.id ?? null
    setZones(previous => previous.filter(zone => zone.id !== id))
    setCopyStatus('')
  }

  function selectSummary() {
    summaryRef.current?.focus()
    summaryRef.current?.select()
  }

  function downloadSummary() {
    // Keep visitor notes out of href/aria attributes that session recorders may capture.
    const url = URL.createObjectURL(new Blob([summary], { type: 'text/plain;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'commercial-window-film-project-checklist.txt'
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  async function copySummary() {
    try {
      await navigator.clipboard.writeText(summary)
      setCopyStatus('Checklist copied. Paste it into your own project notes.')
    } catch {
      selectSummary()
      setCopyStatus('Text selected. Use your device’s Copy command to copy the checklist.')
    }
  }

  return <div className={styles.layout} data-clarity-mask="true">
    <section className={styles.panel} aria-labelledby="zone-editor-heading">
      <p className={styles.step}>01 / Room inventory</p>
      <h2 id="zone-editor-heading">Record your rooms or zones</h2>
      <p>Group windows with the same use and concerns. Split a room into separate zones when the sun exposure or privacy requirement differs.</p>
      <p className={styles.count} role="status" aria-live="polite" aria-atomic="true">{zones.length} of {MAX_PROJECT_ZONES} zones recorded. {zones.length === MAX_PROJECT_ZONES ? 'Zone limit reached.' : 'Add another zone as needed.'}</p>
      <p><a className={styles.jumpLink} href="#scope-summary-heading">Jump to your checklist and download options ↓</a></p>
      {zones.map((zone, index) => <fieldset key={zone.id} className={styles.zone}>
        <legend>Zone {index + 1}</legend>
        <div className={styles.field}>
          <label htmlFor={`zone-${zone.id}-label`}>Room or zone label <span>(optional)</span></label>
          <input id={`zone-${zone.id}-label`} type="text" autoComplete="off" maxLength={80} value={zone.label} placeholder="For example, west-facing meeting room" onChange={event => updateZone(zone.id, { label: event.target.value })} />
        </div>
        <div className={styles.field}>
          <label htmlFor={`zone-${zone.id}-priority`}>Main priority</label>
          <select id={`zone-${zone.id}-priority`} value={zone.priority} onChange={event => updateZone(zone.id, { priority: event.target.value as ProjectZone['priority'] })}>
            {PROJECT_PRIORITIES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor={`zone-${zone.id}-glazing`}>Glass information</label>
          <select id={`zone-${zone.id}-glazing`} value={zone.glazing} aria-describedby={`zone-${zone.id}-glass-help`} onChange={event => updateZone(zone.id, { glazing: event.target.value as ProjectZone['glazing'] })}>
            {PROJECT_GLAZING.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <p id={`zone-${zone.id}-glass-help`} className={styles.small}>Choose unknown if unsure. Add any documented pane or coating details below. All glass and film compatibility requires assessment.</p>
        </div>
        <div className={styles.field}>
          <label htmlFor={`zone-${zone.id}-notes`}>Short notes <span>(optional, up to 400 characters)</span></label>
          <textarea id={`zone-${zone.id}-notes`} rows={3} maxLength={400} value={zone.notes} placeholder="Times of concern, viewing angles, existing blinds or access restrictions" onChange={event => updateZone(zone.id, { notes: event.target.value })} />
        </div>
        <button className={styles.secondary} type="button" disabled={zones.length === 1} aria-label={`Remove zone ${index + 1}`} onClick={() => removeZone(zone.id)}>Remove zone</button>
      </fieldset>)}
      <button className={styles.secondary} type="button" disabled={zones.length >= MAX_PROJECT_ZONES} onClick={addZone}>Add a zone</button>
    </section>
    <section className={`${styles.panel} ${styles.summaryPanel}`} aria-labelledby="scope-summary-heading">
      <p className={styles.step}>02 / Scope for discussion</p>
      <h2 id="scope-summary-heading">Review and keep your checklist</h2>
      <p><a className={styles.jumpLink} href="#zone-editor-heading">Back to room inventory ↑</a></p>
      <p>Your summary updates as you edit. It records your information and questions for assessment; it does not select or approve a film.</p>
      <label className={styles.summaryLabel} htmlFor="project-checklist-summary">Project scope summary (read only)</label>
      <textarea id="project-checklist-summary" className={styles.summaryText} ref={summaryRef} value={summary} readOnly rows={22} spellCheck={false} />
      <div className={styles.actions}>
        <button type="button" className={styles.secondary} onClick={copySummary}>Copy checklist</button>
        <button type="button" className={styles.secondary} onClick={selectSummary}>Select all text</button>
        <button className={styles.secondary} type="button" onClick={downloadSummary}>Download checklist (.txt)</button>
      </div>
      <p className={styles.small} role="status" aria-live="polite">{copyStatus}</p>
      <div className={styles.quote}>
        <h3>Discuss the installation scope</h3>
        <p>This checklist stays on this page. Download or copy it before leaving or reloading. Nothing is saved, submitted or automatically attached to your quote request.</p>
        <a className={styles.primary} href={projectChecklistQuoteHref(zones)} target="_blank" rel="noopener noreferrer">Request a commercial quote <span>(new tab)</span></a>
        <p className={styles.small}>We review commercial requirements before arranging a site assessment. Keep the full checklist for that discussion.</p>
      </div>
    </section>
  </div>
}
